import React, { useState } from 'react';
import { PLACES, MANNERS, CONS } from './data';
import { SagittalHead } from './SagittalHead';
import { MannerDiagram } from './MannerDiagram';
import { VoicingDiagram, SonorityScale } from './VoicingDiagram';
import { Head, Reveal, toneOf, chipCls } from './ui';
import { useT, useL } from '../../lib/lessonI18n';

const Pill = ({ on, onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`rounded-full px-4 py-2 text-[11px] font-bold tracking-wider transition-all ${on ? 'bg-orange-500 text-slate-950' : 'border border-cyan-500/25 bg-slate-900/60 text-cyan-200 hover:text-white hover:border-cyan-400/60'}`}>{children}</button>
);

const Members = ({ list, onJump }) => {
  const L = useL();
  return (
    <div className="flex flex-wrap gap-2">
      {list.map((c) => (
        <button key={c.id} onClick={() => onJump(c.id)} data-testid={`member-${c.id}`}
          className={`rounded-xl px-3 py-2 font-mono text-lg font-black transition-transform hover:-translate-y-0.5 ${chipCls[toneOf(c)]}`}>
          {c.ipa} <span className="ml-1 font-sans text-[11px] font-semibold text-slate-300">{c.key}</span>
        </button>
      ))}
    </div>
  );
};

export const PlaceSection = ({ onJump }) => {
  const T = useT();
  const L = useL();
  const [p, setP] = useState('alveolar');
  const cur = PLACES.find((x) => x.id === p);
  return (
    <section className="py-12" id="ct-place" data-testid="place-section">
      <Head kicker={T('Dimensione 1 · Dove', 'Dimension 1 · Where')} title={T('Luogo di articolazione', 'Place of articulation')} sub={T('Tocca un luogo: la sezione mostra l\'articolatore attivo che raggiunge il suo bersaglio.', 'Tap a place: the section shows the active articulator reaching its target.')} />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_1.1fr] items-center">
        <Reveal className="rounded-3xl border border-cyan-500/15 bg-slate-900/40 p-5">
          <div className="mx-auto aspect-[17/20] w-full max-w-sm"><SagittalHead place={p} labels onPick={setP} testid="place-head" /></div>
        </Reveal>
        <Reveal delay={120} className="space-y-5">
          <div className="flex flex-wrap gap-2">{PLACES.map((x) => <Pill key={x.id} on={p === x.id} onClick={() => setP(x.id)} testid={`place-btn-${x.id}`}>{L(x.label)}</Pill>)}</div>
          <div className="rounded-2xl border border-orange-400/25 bg-slate-900/60 p-5" data-testid="place-detail">
            <p className="text-xl font-black text-white">{L(cur.label)} <span className="text-sm font-semibold text-slate-400">· {L(cur.short)}</span></p>
            <p className="mt-2 text-sm text-slate-300">{L(cur.desc)}</p>
            <div className="mt-4"><Members list={CONS.filter((c) => c.place === p)} onJump={onJump} /></div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export const MannerSection = ({ onJump }) => {
  const T = useT();
  const L = useL();
  const [m, setM] = useState('plosive');
  const cur = MANNERS.find((x) => x.id === m);
  const list = CONS.filter((c) => c.manner === m);
  const demo = list[0];
  return (
    <section className="py-12" id="ct-manner" data-testid="manner-section">
      <Head kicker={T('Dimensione 2 · Come', 'Dimension 2 · How')} title={T('Modo di articolazione', 'Manner of articulation')} sub={T('Quanto si restringe il canale e dove passa l\'aria: guarda il flusso per ogni modo.', 'How much the channel narrows and where the air passes: watch the flow for each manner.')} />
      <Reveal className="mb-6 flex flex-wrap justify-center gap-2">{MANNERS.map((x) => <Pill key={x.id} on={m === x.id} onClick={() => setM(x.id)} testid={`manner-btn-${x.id}`}>{L(x.label)}</Pill>)}</Reveal>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.4fr_1fr] items-center">
        <Reveal className="rounded-3xl border border-cyan-500/15 bg-slate-900/40 p-5">
          <div className="aspect-[2/1] w-full"><MannerDiagram manner={m} /></div>
        </Reveal>
        <Reveal delay={120} className="rounded-3xl border border-cyan-500/15 bg-slate-900/40 p-4 flex items-center gap-4">
          <div className="aspect-[17/20] w-32 shrink-0"><SagittalHead place={demo?.place || 'alveolar'} manner={m} voiced={demo ? demo.voice : true} testid="manner-head" /></div>
          <div className="min-w-0">
            <p className="text-xl font-black text-white">{L(cur.label)}</p>
            <p className="mt-1 text-sm text-slate-300">{L(cur.desc)}</p>
            <div className="mt-3">{list.length ? <Members list={list} onJump={onJump} /> : <p className="font-mono text-lg text-lime-200">[ɾ] water · better · city</p>}</div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export const VoicingSection = () => {
  const T = useT();
  const [v, setV] = useState(true);
  return (
    <section className="py-12" id="ct-voicing" data-testid="voicing-section">
      <Head kicker={T('Dimensione 3 · Con o senza voce', 'Dimension 3 · With or without voice')} title={T('Sonorità e sonoranza', 'Voicing and sonority')} sub={T('Sonorità: vibrano le pliche vocali? Sonoranza: il suono è rumore (ostruente) o risonanza (sonorante)?', 'Voicing: do the vocal folds vibrate? Sonority: is the sound noise (obstruent) or resonance (sonorant)?')} />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Reveal className="min-w-0 rounded-3xl border border-cyan-500/15 bg-slate-900/40 p-5">
          <div className="mb-4 flex justify-center gap-2">
            <Pill on={!v} onClick={() => setV(false)} testid="voicing-btn-voiceless">{T('Sorda · /s/', 'Voiceless · /s/')}</Pill>
            <Pill on={v} onClick={() => setV(true)} testid="voicing-btn-voiced">{T('Sonora · /z/', 'Voiced · /z/')}</Pill>
          </div>
          <div className="aspect-[11/6] w-full"><VoicingDiagram voiced={v} /></div>
          <p className="mt-3 text-sm text-slate-300">{T(<>Metti due dita sulla gola e alterna <b className="text-white">ssss / zzzz</b>: senti la vibrazione solo nella seconda. In inglese le sorde <b className="text-white">fortis</b> /p t k/ sono anche aspirate in sillaba tonica, e accorciano la vocale che le precede (<i>seat</i> vs <i>seed</i>).</>, <>Place two fingers on your throat and alternate <b className="text-white">ssss / zzzz</b>: you feel the vibration only in the second. In English, the voiceless <b className="text-white">fortis</b> /p t k/ are also aspirated in stressed syllables, and shorten the preceding vowel (<i>seat</i> vs <i>seed</i>).</>)}</p>
        </Reveal>
        <Reveal delay={120} className="min-w-0 rounded-3xl border border-cyan-500/15 bg-slate-900/40 p-5">
          <p className="mb-4 text-[11px] font-bold uppercase tracking-widest text-slate-400">{T('Scala di sonoranza', 'Sonority scale')}</p>
          <SonorityScale />
          <div className="mt-4 grid grid-cols-4 gap-2 font-mono text-sm" data-testid="voicing-pairs">
            {[['p', 'b'], ['t', 'd'], ['k', 'ɡ'], ['tʃ', 'dʒ'], ['f', 'v'], ['θ', 'ð'], ['s', 'z'], ['ʃ', 'ʒ']].map(([a, b]) => (
              <span key={a} className="rounded-lg border border-white/5 bg-slate-950/50 px-2 py-1.5 text-center"><span className="text-cyan-200">{a}</span> <span className="text-slate-600">/</span> <span className="text-orange-300">{b}</span></span>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
